import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../utils/api";
import { toast } from "react-toastify";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaCalendarAlt,
  FaHashtag,
  FaEdit,
  FaSave,
  FaTimes,
  FaShieldAlt,
  FaLock,
  FaWallet,
  FaCopy,
  FaCheck,
  FaDollarSign,
  FaChartLine,
  FaUsers,
  FaEye,
  FaEyeSlash,
  FaKey,
  FaLink,
} from "react-icons/fa";

const Profile = () => {
  const { user, updateUser, fetchUserData } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [formData, setFormData] = useState({
    fullName: user?.fullName || "",
    phone: user?.phone || "",
  });

  // Withdrawal security states
  const [withdrawalSettings, setWithdrawalSettings] = useState({
    hasPinSet: false,
    withdrawalAddress: null,
  });
  const [pinFormData, setPinFormData] = useState({
    pin: "",
    password: "",
    currentPin: "",
    newPin: "",
  });
  const [addressFormData, setAddressFormData] = useState({
    address: "",
    pin: "",
  });
  const [showPinForm, setShowPinForm] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [withdrawalLoading, setWithdrawalLoading] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showAddrPin, setShowAddrPin] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.put("/users/profile", formData);
      updateUser(response.data.user);
      toast.success("Profile updated successfully");
      setEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/register?ref=${user?.referralCode}`;
    navigator.clipboard.writeText(link);
    toast.success("Referral link copied!");
  };

  const loadWithdrawalSettings = async () => {
    try {
      const { data } = await api.get("/users/withdrawal-settings");
      setWithdrawalSettings(
        data.settings || { hasPinSet: false, withdrawalAddress: null },
      );
    } catch (error) {
      console.error("Failed to load withdrawal settings", error);
    }
  };

  useEffect(() => {
    loadWithdrawalSettings();
  }, []);

  const handleSetPin = async (e) => {
    e.preventDefault();

    if (!/^\d{4,6}$/.test(pinFormData.pin)) {
      toast.error("PIN must be 4-6 digits");
      return;
    }

    if (!pinFormData.password) {
      toast.error("Password is required");
      return;
    }

    setWithdrawalLoading(true);
    try {
      await api.post("/users/withdrawal-pin", {
        pin: pinFormData.pin,
        currentPassword: pinFormData.password,
      });

      toast.success("Withdrawal PIN set successfully");
      setPinFormData({ pin: "", password: "", currentPin: "", newPin: "" });
      setShowPinForm(false);
      await loadWithdrawalSettings();
      await fetchUserData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to set PIN");
    } finally {
      setWithdrawalLoading(false);
    }
  };

  const handleSetAddress = async (e) => {
    e.preventDefault();

    if (!withdrawalSettings.hasPinSet) {
      toast.error("Set your withdrawal PIN first");
      return;
    }

    if (!/^0x[a-fA-F0-9]{40}$/.test(addressFormData.address)) {
      toast.error("Enter a valid BEP20 address");
      return;
    }

    if (!addressFormData.pin) {
      toast.error("Withdrawal PIN is required");
      return;
    }

    setWithdrawalLoading(true);
    try {
      await api.post("/users/withdrawal-address", {
        address: addressFormData.address,
        pin: addressFormData.pin,
      });

      toast.success("Withdrawal address saved");
      setAddressFormData({ address: "", pin: "" });
      setShowAddressForm(false);
      await loadWithdrawalSettings();
      await fetchUserData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save address");
    } finally {
      setWithdrawalLoading(false);
    }
  };

  return (
    <div className="space-y-5 pb-6">
      {/* Hero Card */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-500 via-violet-600 to-purple-600 p-5 shadow-xl shadow-violet-500/10">
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-white/10 blur-xl" />
        <div className="relative z-10">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center text-white text-xl font-extrabold border border-white/20">
              {user?.fullName?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-white truncate">
                {user?.fullName}
              </h1>
              <p className="text-[12px] text-violet-100/80 font-medium">
                @{user?.username}
              </p>
            </div>
            {!editing && (
              <button
                onClick={() => setEditing(true)}
                className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center active:scale-[0.95] transition-transform touch-manipulation"
              >
                <FaEdit className="text-sm text-white" />
              </button>
            )}
          </div>

          <div className="bg-white/15 backdrop-blur-xl rounded-xl px-4 py-3 border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] text-violet-100 font-medium uppercase tracking-wider">
                  Wallet Balance
                </p>
                <p className="text-2xl font-extrabold text-white tracking-tight mt-0.5">
                  ${user?.walletBalance?.toFixed(2) || "0.00"}
                </p>
              </div>
              <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center">
                <FaWallet className="text-lg text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Account Status Chips */}
      <div className="relative">
        <div className="absolute top-0 right-0 bottom-1 w-8 bg-gradient-to-l from-slate-950 to-transparent z-10 pointer-events-none rounded-r-2xl" />
        <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
          {[
            {
              label: "Status",
              value: user?.isActivated ? "Active" : "Inactive",
              icon: FaCheck,
              gradient:
                user?.isActivated ?
                  "from-emerald-500 to-teal-600"
                : "from-amber-500 to-orange-500",
              glow:
                user?.isActivated ?
                  "shadow-emerald-500/20"
                : "shadow-amber-500/20",
            },
            {
              label: "Stage",
              value: `${user?.currentStage || 0} / 26`,
              icon: FaChartLine,
              gradient: "from-sky-500 to-cyan-600",
              glow: "shadow-sky-500/20",
            },
            {
              label: "Recruits",
              value: user?.directRecruits || 0,
              icon: FaUsers,
              gradient: "from-violet-500 to-purple-600",
              glow: "shadow-violet-500/20",
            },
            {
              label: "Earnings",
              value: `$${user?.totalEarnings?.toFixed(2) || "0.00"}`,
              icon: FaDollarSign,
              gradient: "from-emerald-500 to-green-600",
              glow: "shadow-emerald-500/20",
            },
          ].map((stat, i) => {
            const Icon = stat.icon;
            return (
              <div
                key={i}
                className={`snap-start flex-shrink-0 w-[120px] rounded-xl bg-gradient-to-br ${stat.gradient} p-3.5 shadow-lg ${stat.glow} border border-white/10`}
              >
                <Icon className="text-white/70 text-xs mb-2" />
                <p className="text-lg font-extrabold text-white leading-none">
                  {stat.value}
                </p>
                <p className="text-[10px] text-white/70 font-medium mt-1">
                  {stat.label}
                </p>
              </div>
            );
          })}
          <div className="flex-shrink-0 w-4" />
        </div>
      </div>

      {/* Personal Info / Edit Form */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4">
        <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
          <FaUser className="text-cyan-400 text-xs" />
          Personal Information
        </h3>

        {editing ?
          <form onSubmit={handleSubmit} className="space-y-3">
            {[
              {
                name: "fullName",
                label: "Full Name",
                icon: FaUser,
                type: "text",
              },
              { name: "phone", label: "Phone", icon: FaPhone, type: "tel" },
            ].map((field) => {
              const Icon = field.icon;
              return (
                <div key={field.name}>
                  <label className="text-[11px] text-slate-500 font-medium uppercase tracking-wider mb-1.5 block">
                    {field.label}
                  </label>
                  <div
                    className={`flex items-center rounded-xl border bg-white/[0.04] transition-all duration-200 ${
                      focusedField === field.name ?
                        "border-cyan-400/50 shadow-[0_0_0_3px_rgba(34,211,238,0.08)]"
                      : "border-white/10"
                    }`}
                  >
                    <div className="pl-3.5 pr-2">
                      <Icon className="text-slate-500 text-xs" />
                    </div>
                    <input
                      type={field.type}
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange}
                      onFocus={() => setFocusedField(field.name)}
                      onBlur={() => setFocusedField(null)}
                      className="flex-1 bg-transparent text-sm text-white py-3 pr-3.5 outline-none placeholder-slate-600"
                      autoComplete="off"
                      required
                    />
                  </div>
                </div>
              );
            })}
            <div className="flex gap-2 pt-1">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-emerald-500 text-white text-xs font-semibold active:scale-[0.97] transition-transform touch-manipulation disabled:opacity-50"
              >
                {loading ?
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                : <>
                    <FaSave className="text-[10px]" />
                    Save Changes
                  </>
                }
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setFormData({
                    fullName: user?.fullName || "",
                    phone: user?.phone || "",
                  });
                }}
                className="px-4 py-2.5 rounded-xl bg-white/[0.06] border border-white/10 text-slate-400 text-xs font-semibold active:scale-[0.97] transition-transform touch-manipulation"
              >
                <FaTimes className="text-xs" />
              </button>
            </div>
          </form>
        : <div className="space-y-2">
            {[
              { label: "Username", value: `@${user?.username}`, icon: FaUser },
              { label: "Full Name", value: user?.fullName, icon: FaUser },
              { label: "Email", value: user?.email, icon: FaEnvelope },
              { label: "Phone", value: user?.phone, icon: FaPhone },
              {
                label: "Member Since",
                value:
                  user?.createdAt ?
                    new Date(user.createdAt).toLocaleDateString()
                  : "N/A",
                icon: FaCalendarAlt,
              },
              {
                label: "Position",
                value: `#${user?.positionInChain}`,
                icon: FaHashtag,
              },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div
                  key={i}
                  className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.05]"
                >
                  <div className="w-8 h-8 rounded-lg bg-white/[0.06] flex items-center justify-center flex-shrink-0">
                    <Icon className="text-xs text-slate-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">
                      {item.label}
                    </p>
                    <p className="text-sm font-semibold text-white truncate">
                      {item.value}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        }
      </div>

      {/* Withdrawal Security */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <FaShieldAlt className="text-cyan-400 text-xs" />
            Withdrawal Security
          </h3>
          {!withdrawalSettings.hasPinSet && (
            <button
              onClick={() => {
                setShowPinForm(true);
                setShowAddressForm(false);
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-cyan-500 to-emerald-500 text-white text-[11px] font-semibold active:scale-[0.97] transition-transform touch-manipulation"
            >
              <FaKey className="text-[9px]" />
              Set PIN
            </button>
          )}
          {withdrawalSettings.hasPinSet &&
            !withdrawalSettings.withdrawalAddress && (
              <button
                onClick={() => {
                  setShowAddressForm((prev) => !prev);
                  setShowPinForm(false);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-cyan-500 to-emerald-500 text-white text-[11px] font-semibold active:scale-[0.97] transition-transform touch-manipulation"
              >
                <FaWallet className="text-[9px]" />
                {showAddressForm ? "Hide" : "Set Address"}
              </button>
            )}
        </div>

        {/* Security Status */}
        <div className="space-y-2 mb-3">
          <div className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.05]">
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                withdrawalSettings.hasPinSet ? "bg-emerald-500/15" : (
                  "bg-amber-500/15"
                )
              }`}
            >
              <FaLock
                className={`text-xs ${withdrawalSettings.hasPinSet ? "text-emerald-400" : "text-amber-400"}`}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white">Withdrawal PIN</p>
              <p className="text-[11px] text-slate-500">
                {withdrawalSettings.hasPinSet ?
                  "Locked — contact support to change"
                : "Required for withdrawals"}
              </p>
            </div>
            <span
              className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                withdrawalSettings.hasPinSet ?
                  "text-emerald-300 bg-emerald-500/15 border-emerald-500/30"
                : "text-amber-300 bg-amber-500/15 border-amber-500/30"
              }`}
            >
              {withdrawalSettings.hasPinSet ? "Set" : "Not Set"}
            </span>
          </div>

          <div className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.05]">
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                withdrawalSettings.withdrawalAddress ? "bg-emerald-500/15" : (
                  "bg-amber-500/15"
                )
              }`}
            >
              <FaWallet
                className={`text-xs ${withdrawalSettings.withdrawalAddress ? "text-emerald-400" : "text-amber-400"}`}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white">
                Withdrawal Address
              </p>
              {withdrawalSettings.withdrawalAddress ?
                <p className="text-[11px] text-cyan-300 font-mono truncate">
                  {withdrawalSettings.withdrawalAddress}
                </p>
              : <p className="text-[11px] text-slate-500">
                  Set once with your PIN
                </p>
              }
            </div>
            <span
              className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                withdrawalSettings.withdrawalAddress ?
                  "text-emerald-300 bg-emerald-500/15 border-emerald-500/30"
                : "text-amber-300 bg-amber-500/15 border-amber-500/30"
              }`}
            >
              {withdrawalSettings.withdrawalAddress ? "Locked" : "Not Set"}
            </span>
          </div>
        </div>

        {/* Set PIN Form */}
        {showPinForm && !withdrawalSettings.hasPinSet && (
          <form
            onSubmit={handleSetPin}
            className="space-y-3 pt-3 border-t border-white/[0.06]"
          >
            <div>
              <label className="text-[11px] text-slate-500 font-medium uppercase tracking-wider mb-1.5 block">
                New PIN (4-6 digits)
              </label>
              <div
                className={`flex items-center rounded-xl border bg-white/[0.04] transition-all duration-200 ${
                  focusedField === "pin" ?
                    "border-cyan-400/50 shadow-[0_0_0_3px_rgba(34,211,238,0.08)]"
                  : "border-white/10"
                }`}
              >
                <div className="pl-3.5 pr-2">
                  <FaLock className="text-slate-500 text-xs" />
                </div>
                <input
                  type={showPin ? "text" : "password"}
                  value={pinFormData.pin}
                  onChange={(e) =>
                    setPinFormData((prev) => ({ ...prev, pin: e.target.value }))
                  }
                  onFocus={() => setFocusedField("pin")}
                  onBlur={() => setFocusedField(null)}
                  className="flex-1 bg-transparent text-sm text-white py-3 outline-none placeholder-slate-600"
                  placeholder="Enter PIN"
                  maxLength={6}
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPin(!showPin)}
                  className="px-3 text-slate-500"
                >
                  {showPin ?
                    <FaEyeSlash className="text-xs" />
                  : <FaEye className="text-xs" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-[11px] text-slate-500 font-medium uppercase tracking-wider mb-1.5 block">
                Account Password
              </label>
              <div
                className={`flex items-center rounded-xl border bg-white/[0.04] transition-all duration-200 ${
                  focusedField === "password" ?
                    "border-cyan-400/50 shadow-[0_0_0_3px_rgba(34,211,238,0.08)]"
                  : "border-white/10"
                }`}
              >
                <div className="pl-3.5 pr-2">
                  <FaKey className="text-slate-500 text-xs" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  value={pinFormData.password}
                  onChange={(e) =>
                    setPinFormData((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                  onFocus={() => setFocusedField("password")}
                  onBlur={() => setFocusedField(null)}
                  className="flex-1 bg-transparent text-sm text-white py-3 outline-none placeholder-slate-600"
                  placeholder="Login password"
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="px-3 text-slate-500"
                >
                  {showPassword ?
                    <FaEyeSlash className="text-xs" />
                  : <FaEye className="text-xs" />}
                </button>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={withdrawalLoading}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-emerald-500 text-white text-xs font-semibold active:scale-[0.97] transition-transform touch-manipulation disabled:opacity-50"
              >
                {withdrawalLoading ?
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                : <>
                    <FaSave className="text-[10px]" /> Save PIN
                  </>
                }
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowPinForm(false);
                  setPinFormData({
                    pin: "",
                    password: "",
                    currentPin: "",
                    newPin: "",
                  });
                }}
                className="px-4 py-2.5 rounded-xl bg-white/[0.06] border border-white/10 text-slate-400 text-xs font-semibold active:scale-[0.97] transition-transform touch-manipulation"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Set Address Form */}
        {showAddressForm &&
          withdrawalSettings.hasPinSet &&
          !withdrawalSettings.withdrawalAddress && (
            <form
              onSubmit={handleSetAddress}
              className="space-y-3 pt-3 border-t border-white/[0.06]"
            >
              <div>
                <label className="text-[11px] text-slate-500 font-medium uppercase tracking-wider mb-1.5 block">
                  BEP20 Address (set once)
                </label>
                <div
                  className={`flex items-center rounded-xl border bg-white/[0.04] transition-all duration-200 ${
                    focusedField === "address" ?
                      "border-cyan-400/50 shadow-[0_0_0_3px_rgba(34,211,238,0.08)]"
                    : "border-white/10"
                  }`}
                >
                  <div className="pl-3.5 pr-2">
                    <FaWallet className="text-slate-500 text-xs" />
                  </div>
                  <input
                    type="text"
                    value={addressFormData.address}
                    onChange={(e) =>
                      setAddressFormData((prev) => ({
                        ...prev,
                        address: e.target.value,
                      }))
                    }
                    onFocus={() => setFocusedField("address")}
                    onBlur={() => setFocusedField(null)}
                    className="flex-1 bg-transparent text-sm text-white py-3 pr-3.5 outline-none placeholder-slate-600 font-mono"
                    placeholder="0x..."
                    autoComplete="off"
                    required
                  />
                </div>
                <p className="text-[10px] text-slate-500 mt-1.5">
                  Address will be locked permanently
                </p>
              </div>
              <div>
                <label className="text-[11px] text-slate-500 font-medium uppercase tracking-wider mb-1.5 block">
                  Withdrawal PIN
                </label>
                <div
                  className={`flex items-center rounded-xl border bg-white/[0.04] transition-all duration-200 ${
                    focusedField === "addrPin" ?
                      "border-cyan-400/50 shadow-[0_0_0_3px_rgba(34,211,238,0.08)]"
                    : "border-white/10"
                  }`}
                >
                  <div className="pl-3.5 pr-2">
                    <FaLock className="text-slate-500 text-xs" />
                  </div>
                  <input
                    type={showAddrPin ? "text" : "password"}
                    value={addressFormData.pin}
                    onChange={(e) =>
                      setAddressFormData((prev) => ({
                        ...prev,
                        pin: e.target.value,
                      }))
                    }
                    onFocus={() => setFocusedField("addrPin")}
                    onBlur={() => setFocusedField(null)}
                    className="flex-1 bg-transparent text-sm text-white py-3 outline-none placeholder-slate-600"
                    placeholder="Enter PIN"
                    maxLength={6}
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowAddrPin(!showAddrPin)}
                    className="px-3 text-slate-500"
                  >
                    {showAddrPin ?
                      <FaEyeSlash className="text-xs" />
                    : <FaEye className="text-xs" />}
                  </button>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={withdrawalLoading}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-emerald-500 text-white text-xs font-semibold active:scale-[0.97] transition-transform touch-manipulation disabled:opacity-50"
                >
                  {withdrawalLoading ?
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  : <>
                      <FaSave className="text-[10px]" /> Save Address
                    </>
                  }
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddressForm(false);
                    setAddressFormData({ address: "", pin: "" });
                  }}
                  className="px-4 py-2.5 rounded-xl bg-white/[0.06] border border-white/10 text-slate-400 text-xs font-semibold active:scale-[0.97] transition-transform touch-manipulation"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
      </div>

      {/* Referral Link Card */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4">
        <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
          <FaLink className="text-cyan-400 text-xs" />
          Your Referral Link
        </h3>
        <div className="flex items-center gap-2">
          <div className="flex-1 px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/10 overflow-hidden">
            <p className="text-xs text-slate-400 font-mono truncate">
              {window.location.origin}/register?ref={user?.referralCode}
            </p>
          </div>
          <button
            onClick={copyReferralLink}
            className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-emerald-500 text-white text-xs font-semibold active:scale-[0.97] transition-transform touch-manipulation"
          >
            <FaCopy className="text-[10px]" />
            Copy
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          Code:{" "}
          <span className="text-cyan-300 font-semibold">
            {user?.referralCode}
          </span>
        </p>
      </div>

      {/* Earnings Summary */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4">
        <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
          <FaDollarSign className="text-cyan-400 text-xs" />
          Earnings Summary
        </h3>
        <div className="space-y-2">
          {[
            {
              label: "Referral Earnings",
              value: `$${user?.referralEarnings?.toFixed(2) || "0.00"}`,
              icon: FaUsers,
              color: "text-emerald-400",
              bg: "bg-emerald-500/10",
            },
            {
              label: "Stage Earnings",
              value: `$${user?.stageEarnings?.toFixed(2) || "0.00"}`,
              icon: FaChartLine,
              color: "text-cyan-400",
              bg: "bg-cyan-500/10",
            },
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <div
                key={i}
                className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.05]"
              >
                <div
                  className={`w-8 h-8 rounded-lg ${item.bg} flex items-center justify-center flex-shrink-0`}
                >
                  <Icon className={`text-xs ${item.color}`} />
                </div>
                <div className="flex-1">
                  <p className="text-[11px] text-slate-500">{item.label}</p>
                </div>
                <p className={`text-sm font-bold ${item.color}`}>
                  {item.value}
                </p>
              </div>
            );
          })}
          <div className="flex items-center gap-3 px-3.5 py-3 rounded-xl bg-gradient-to-r from-cyan-500/10 to-emerald-500/10 border border-cyan-500/20">
            <div className="w-8 h-8 rounded-lg bg-cyan-500/15 flex items-center justify-center flex-shrink-0">
              <FaWallet className="text-xs text-cyan-400" />
            </div>
            <div className="flex-1">
              <p className="text-[11px] text-cyan-200/70 font-medium">
                Wallet Balance
              </p>
            </div>
            <p className="text-lg font-extrabold text-cyan-300">
              ${user?.walletBalance?.toFixed(2) || "0.00"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
